import { useMemo } from 'react';
import { NavigateOptions, useLocation, useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';

import { useAutoUpdatedRef } from '@sternum/web/hooks/useAutoUpdatedRef';

export interface UseSearchParamStateOptions<StateType> {
  navigateOptions?: NavigateOptions;
  mapSearchParamStringToValue?: (searchParamValue: string | null | undefined) => StateType;
  mapSetStateValueToString?: (stateNewValue: StateType, statePrevValue: StateType) => string | undefined;
}

export function useSearchParamState<StateType extends [] | object | string | boolean | number | null | undefined>(
  searchKey: string,
  options: UseSearchParamStateOptions<StateType> = {},
): [StateType, (stateValueToUpdate: StateType | ((currentValue: StateType) => StateType)) => unknown] {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { state } = useLocation();
  const optionsRef = useAutoUpdatedRef(options);

  const searchParamsValue = searchParams.get(searchKey);
  const stateValue = useMemo(() => {
    return optionsRef.current?.mapSearchParamStringToValue
      ? optionsRef.current?.mapSearchParamStringToValue(searchParamsValue)
      : searchParamsValue;
  }, [optionsRef, searchParamsValue]) as StateType;
  const stateValueRef = useAutoUpdatedRef(stateValue);

  const setStateValueRef = useAutoUpdatedRef(
    (stateValueToUpdateArg: StateType | ((currentValue: StateType) => StateType)) => {
      const stateValueToUpdate =
        typeof stateValueToUpdateArg === 'function'
          ? stateValueToUpdateArg(stateValueRef.current!)
          : stateValueToUpdateArg;
      const valueToUpdate = optionsRef.current?.mapSetStateValueToString
        ? optionsRef.current?.mapSetStateValueToString(stateValueToUpdate, stateValueRef.current!)
        : stateValueToUpdate;
      const urlObj = new URL(document.location.toString());
      const params = urlObj.searchParams;

      if (valueToUpdate === undefined) {
        params.delete(searchKey);
      } else {
        params.set(searchKey, `${valueToUpdate}`);
      }

      navigate(
        {
          pathname: urlObj.pathname,
          hash: urlObj.hash,
          search: params.toString(),
        },
        {
          state,
          replace: true,
          ...optionsRef.current?.navigateOptions,
        },
      );
    },
  );

  return [stateValue, setStateValueRef.current!];
}

useSearchParamState.mapSearchParamStringToBoolean = (searchParamValue: string | null | undefined): boolean => {
  return searchParamValue === 'true';
};

useSearchParamState.mapBooleanToSearchParamString = (stateValue: boolean | null | undefined): string | undefined => {
  return stateValue ? `${stateValue}` : undefined;
};

useSearchParamState.mapSearchParamStringToNumber = (searchParamValue: string | null | undefined): number => {
  return +(searchParamValue || '');
};

useSearchParamState.mapSearchParamStringToString = (searchParamValue: string | null | undefined) => {
  return searchParamValue || '';
};

useSearchParamState.mapStringToSearchParamString = (stateValue: string | null | undefined): string | undefined => {
  return stateValue || undefined;
};

useSearchParamState.mapSearchParamStringToObject = (searchParamValue: string | null | undefined) => {
  return searchParamValue ? JSON.parse(searchParamValue) : {};
};

useSearchParamState.mapObjectToSearchParamString = <T extends object>(stateValue: T | null | undefined) => {
  const stringValue = JSON.stringify(stateValue || {});

  return stringValue === '{}' ? undefined : stringValue;
};

useSearchParamState.mapSearchParamStringToArray = (searchParamValue: string | null | undefined) => {
  return searchParamValue ? JSON.parse(searchParamValue) : [];
};

useSearchParamState.mapArrayToSearchParamString = <T extends object>(stateValue: T | null | undefined) => {
  const stringValue = JSON.stringify(stateValue || []);

  return stringValue === '[]' ? undefined : stringValue;
};
